import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  Clock,
  CheckCircle2,
  AlertTriangle,
  Inbox,
  ArrowRight,
  UserPlus,
  Calendar,
} from 'lucide-react';
import { api, extractApiError } from '@/lib/api';
import { toast } from '@/stores/toast';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/ui/empty-state';
import { formatDate, cn } from '@/lib/utils';
import { useAuthStore } from '@/stores/auth';

interface QueueRun {
  id: string;
  status: string;
  assignedTo: string | null;
  dueBy: string | null;
  createdAt: string;
  startedAt: string | null;
  totalCost: number;
  workflow: { name: string; type?: string };
  documents: Array<{ document: { id: string; fileName: string; documentType: string | null } }>;
  // The seed writes a human-friendly summary to outputData.summary and a
  // case/signal reference to inputParams when no Document is attached
  // (Signal Validation, MedDRA Coding Review). We fall back to those so
  // the queue row never renders a bare em-dash.
  inputParams: {
    caseNumber?: string;
    signalNumber?: string;
    subtitle?: string;
    [k: string]: unknown;
  } | null;
  outputData: {
    summary?: string;
    [k: string]: unknown;
  } | null;
}

/**
 * Build the secondary line shown under each queue row. Prefers an attached
 * Document filename, then case/signal references from inputParams, then the
 * outputData summary, then a generic em-dash.
 */
function queueSubtitle(r: QueueRun): string {
  const docNames = r.documents?.map((d) => d.document.fileName).filter(Boolean).join(', ');
  if (docNames) return docNames;
  const ip = r.inputParams ?? {};
  if (ip.caseNumber) return `Case ${ip.caseNumber}`;
  if (ip.signalNumber) return `Signal ${ip.signalNumber}`;
  if (ip.subtitle) return String(ip.subtitle);
  if (r.outputData?.summary) return r.outputData.summary;
  return '—';
}

export function MyQueuePage() {
  const qc = useQueryClient();
  const user = useAuthStore((s) => s.user);

  const queue = useQuery({
    queryKey: ['queue', 'mine'],
    queryFn: async () => (await api.get('/api/queues/mine')).data,
    refetchInterval: 30000,
  });

  const claim = useMutation({
    mutationFn: async (runId: string) => api.post(`/api/queues/claim/${runId}`, {}),
    onSuccess: () => {
      toast.success('Claimed', 'Added to your queue');
      qc.invalidateQueries({ queryKey: ['queue'] });
    },
    onError: (err) => toast.error('Claim failed', extractApiError(err).message),
  });

  const items = (queue.data?.items as QueueRun[]) ?? [];
  const mine = items.filter((i) => i.assignedTo === user?.id);
  const unassigned = items.filter((i) => !i.assignedTo);
  const overdue = mine.filter((i) => i.dueBy && new Date(i.dueBy) < new Date());

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-2xl">My review queue</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Workflow runs that need your e-signature. Items you've claimed plus anything unassigned
          your role can pick up.
        </p>
      </div>

      <div className="grid gap-3 md:grid-cols-3">
        <Stat icon={Inbox} label="Assigned to you" value={mine.length} />
        <Stat icon={AlertTriangle} label="Overdue" value={overdue.length} tone={overdue.length > 0 ? 'danger' : 'muted'} />
        <Stat icon={UserPlus} label="Unassigned (claimable)" value={unassigned.length} />
      </div>

      {queue.isLoading ? (
        <Skeleton className="h-64" />
      ) : items.length === 0 ? (
        <EmptyState
          icon={CheckCircle2}
          title="You're all caught up"
          description="No reviews waiting. New ones will appear here automatically when workflows reach their review checkpoint."
        />
      ) : (
        <div className="space-y-4">
          {mine.length > 0 && (
            <Section title="Assigned to you" items={mine} onClaim={() => {}} user={user} />
          )}
          {unassigned.length > 0 && (
            <Section title="Unassigned — click to claim" items={unassigned} onClaim={(id) => claim.mutate(id)} user={user} claimable />
          )}
        </div>
      )}
    </div>
  );
}

function Section({
  title,
  items,
  onClaim,
  user,
  claimable,
}: {
  title: string;
  items: QueueRun[];
  onClaim: (id: string) => void;
  user: ReturnType<typeof useAuthStore.getState>['user'];
  claimable?: boolean;
}) {
  return (
    <Card>
      <CardContent className="p-0">
        <div className="border-b px-4 py-2 text-xs font-medium uppercase tracking-wider text-muted-foreground">
          {title}
        </div>
        <div className="divide-y">
          {items.map((r) => {
            const overdue = r.dueBy && new Date(r.dueBy) < new Date();
            const due = r.dueBy ? new Date(r.dueBy) : null;
            const subtitle = queueSubtitle(r);
            return (
              <div key={r.id} className={cn('flex items-center gap-4 px-4 py-3', overdue && 'bg-destructive/5')}>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <Link to={`/workflows/runs/${r.id}`} className="font-medium hover:text-primary">
                      {r.workflow.name}
                    </Link>
                    {overdue && <Badge variant="destructive">Overdue</Badge>}
                  </div>
                  <div className="mt-0.5 truncate text-xs text-muted-foreground" title={subtitle}>
                    {subtitle}
                  </div>
                </div>
                <div className="hidden text-right text-xs md:block">
                  <div className={cn('flex items-center gap-1 justify-end', overdue ? 'text-destructive font-medium' : 'text-muted-foreground')}>
                    <Clock className="h-3 w-3" />
                    {due ? (overdue ? `Overdue ${formatDate(due)}` : `Due ${formatDate(due)}`) : 'No deadline'}
                  </div>
                  <div className="mt-0.5 text-muted-foreground">Started {r.startedAt ? formatDate(r.startedAt) : formatDate(r.createdAt)}</div>
                </div>
                {claimable ? (
                  <Button size="sm" onClick={() => onClaim(r.id)} disabled={!user}>
                    <UserPlus className="mr-1.5 h-3.5 w-3.5" /> Claim
                  </Button>
                ) : (
                  <Button size="sm" asChild>
                    <Link to={`/workflows/runs/${r.id}`}>
                      Review <ArrowRight className="ml-1 h-3 w-3" />
                    </Link>
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

function Stat({
  icon: Icon,
  label,
  value,
  tone = 'muted',
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: number;
  tone?: 'muted' | 'warning' | 'danger';
}) {
  const toneCls = {
    muted: 'bg-muted/40 text-muted-foreground',
    warning: 'bg-amber-500/10 text-amber-600',
    danger: 'bg-destructive/10 text-destructive',
  }[tone];
  return (
    <Card>
      <CardContent className="flex items-start justify-between p-4">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
          <div className="mt-1 font-display text-2xl">{value}</div>
        </div>
        <div className={cn('flex h-9 w-9 items-center justify-center rounded-md', toneCls)}>
          <Icon className="h-4 w-4" />
        </div>
      </CardContent>
    </Card>
  );
}

// unused; keeps tree-shaking happy
void Calendar;
